import type { Editor } from '@tiptap/core';
import { Fragment, type MutableRefObject } from 'react';
import { BlockView } from '../blocks/BlockView';
import type { BlockPatch, BlockType, DeviceMode, EmailBlock } from '../HtmlEditor.types';
import { DropZone } from './DropZone';

type CanvasProps = {
  blocks: EmailBlock[];
  selectedId: string | null;
  device: DeviceMode;
  dragTypeRef: MutableRefObject<BlockType | null>;
  onSelect: (id: string | null) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onTextEdit: (id: string, text: string) => void;
  onUpdateBlock: (id: string, patch: BlockPatch) => void;
  onInsertAt: (idx: number, type: BlockType) => void;
  onEditorReady?: (editor: Editor | null) => void;
  emailWidth?: number;
  disabled?: boolean;
};

export function Canvas({
  blocks,
  selectedId,
  device,
  dragTypeRef,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onTextEdit,
  onUpdateBlock,
  onInsertAt,
  onEditorReady,
  emailWidth = 600,
  disabled,
}: CanvasProps) {
  const handleDrop = (idx: number) => {
    if (dragTypeRef.current) {
      onInsertAt(idx, dragTypeRef.current);
      dragTypeRef.current = null;
    }
  };

  const canvasWidth = device === 'mobile' ? 360 : emailWidth;

  return (
    <main className='ee-canvas-wrap' onClick={() => onSelect(null)}>
      <div
        className={`ee-canvas ${device === 'mobile' ? 'is-mobile' : ''}`}
        style={{ width: canvasWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <DropZone onDrop={() => handleDrop(0)} disabled={disabled} />

        {blocks.map((b, i) => (
          <Fragment key={b.id}>
            <BlockView
              block={b}
              selected={selectedId === b.id}
              disabled={disabled}
              onSelect={() => onSelect(b.id)}
              onMoveUp={() => onMoveUp(b.id)}
              onMoveDown={() => onMoveDown(b.id)}
              onDuplicate={() => onDuplicate(b.id)}
              onDelete={() => onDelete(b.id)}
              onTextEdit={(text) => onTextEdit(b.id, text)}
              onChange={(patch) => onUpdateBlock(b.id, patch)}
              onEditorReady={selectedId === b.id ? onEditorReady : undefined}
            />
            <DropZone onDrop={() => handleDrop(i + 1)} disabled={disabled} />
          </Fragment>
        ))}
      </div>
    </main>
  );
}
